import { ReportGroup } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTenants } from '@/services/tenants/useTenants';
import { useUpdateReportGroupName } from '@/hooks/api/reports/useUpdateReportGroupName';
import { useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { TreeBranch, GroupSampleHeight } from './ReportSample.styles';
import { GroupThumbnails } from './GroupThumbnails';
import { GroupNameEditor } from './GroupNameEditor';
import { GroupNameDisplay } from './GroupNameDisplay';
import { CardWrapper } from './CardWrapper';
import { useDroppable } from '@dnd-kit/core';
import { MergeGroupModal } from '@/components/modals/MergeGroupModal/MergeGroupModal';
import { DeleteGroupModal } from '@/components/modals/DeleteGroupModal';

export const ReportSample = ({
  group,
  groupIndex,
  isSelected,
  isLastItem,
  isDragging,
}: {
  group: ReportGroup;
  groupIndex: number;
  isSelected: boolean;
  isLastItem?: boolean;
  isDragging?: boolean;
}) => {
  const { id } = useParams();
  const { getPath } = useTenants();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(group.name || `Group ${groupIndex}`);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<{ mouseX: number; mouseY: number } | undefined>();
  const showToast = useToast(state => state.showToast);

  const { mutate: updateGroupName, isPending: isUpdatingGroupName } = useUpdateReportGroupName({
    onSuccess: () => {
      showToast({ message: 'Group name updated successfully', type: 'success' });
    },
    onError: error => {
      showToast({ message: error, type: 'error' });
    },
  });

  const groupName = group.name || `Group ${groupIndex}`;

  const handleSave = (event?: React.MouseEvent<HTMLButtonElement | HTMLInputElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (id && group.id) {
      updateGroupName({
        reportId: id,
        groupId: group.id,
        name: editedName.trim(),
      });
      setIsEditing(false);
    }
  };

  const handleCancel = (event?: React.MouseEvent<HTMLButtonElement | HTMLInputElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setEditedName(groupName);
    setIsEditing(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const handleEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsEditing(true);
  };

  const handleMerge = () => {
    setIsMergeModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(undefined);
  };

  const { setNodeRef, isOver } = useDroppable({
    id: group.id,
    data: {
      type: 'report-group',
      groupId: group.id,
    },
  });

  return (
    <Box display='flex' alignItems='stretch' height={GroupSampleHeight} sx={{ minWidth: 0, overflow: 'visible' }}>
      <TreeBranch isLastItem={isLastItem} />
      <CardWrapper
        ref={setNodeRef}
        isSelected={isSelected}
        isEditing={isEditing}
        to={getPath(`report/${id}/${group.id}`)}
        isDragOver={isOver && !isSelected}
        isDragging={isDragging && !isSelected}
        onContextMenu={handleContextMenu}
      >
        {isEditing || isUpdatingGroupName ? (
          <GroupNameEditor
            editedName={editedName}
            setEditedName={setEditedName}
            handleSave={handleSave}
            isUpdatingGroupName={isUpdatingGroupName}
            handleCancel={handleCancel}
            handleKeyPress={handleKeyPress}
          />
        ) : (
          <GroupNameDisplay
            groupName={groupName}
            onEdit={handleEdit}
            onMerge={handleMerge}
            onDelete={handleDelete}
            contextMenuAnchor={contextMenuAnchor}
            onContextMenuClose={handleContextMenuClose}
          />
        )}
        <BodyText marginBottom='4px'>{group.assetIds.length} assets</BodyText>
        <GroupThumbnails group={group} />
      </CardWrapper>
      {id && (
        <>
          <MergeGroupModal
            open={isMergeModalOpen}
            onClose={() => setIsMergeModalOpen(false)}
            sourceGroup={group}
            reportId={id}
          />
          <DeleteGroupModal
            open={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            group={group}
            groupName={groupName}
            reportId={id}
          />
        </>
      )}
    </Box>
  );
};
